export interface IUserStoreState {
	user: IUserModel | null
}

export interface IUserStore {
	state: Readonly<IUserStoreState>
	setUser: (user: IUserModel) => void
	logout: () => Promise<void>
	register: (model: IRegister) => Promise<void>
	login: (email: string, password: string) => Promise<void>
	fetchUser: () => Promise<void>
	getUser: ComputedRef<IUserModel | null>
	hasPermission: (permission: EEmployeePermission | EEmployeePermission[] | null) => boolean
	getPermissions: ComputedRef<EEmployeePermission[]>
	isSuperAdmin: () => boolean
}

export const useUserStore = defineStore('userStore', (): IUserStore => {
	const _state = reactive<IUserStoreState>({ user: null })

	const setUser = (user: IUserModel) => {
		_state.user = user
	}

	const fetchUser = async () => {
		const client = await ApiService.fetchCurrentEmployee()
		setUser(client)
	}

	const login = async (email: string, password: string): Promise<void> => {
		const user = await ApiService.login(email, password)
		useCookie('is_logged').value = 'true'
		_state.user = user
		await fetchUser()
	}

	const register = async (model: IRegister): Promise<void> => {
		const user = await ApiService.register(model)
		useCookie('is_logged').value = 'true'
		_state.user = user
		await fetchUser()
	}

	const logout = async () => {
		_state.user = null
		useCookie('is_logged').value = 'false'

		try {
			await ApiService.logout()
		} catch {
			// Ignore API logout failure
		}
	}

	const getUser = computed(() => _state.user)

	const getPermissions = computed(() => _state.user?.permissions || [])

	const hasPermission = (permission: EEmployeePermission | EEmployeePermission[] | null) => {
		if (!permission) return true
		if (Array.isArray(permission)) {
			return permission.some(perm => _state.user?.permissions?.includes(perm))
		}
		return _state.user?.permissions ? _state.user.permissions.includes(permission) : false
	}

	const isSuperAdmin = () => _state.user?.role === EEmployeeRole.SUPER_ADMIN

	return {
		state: _state,
		setUser,
		login,
		register,
		logout,
		fetchUser,
		getUser,
		hasPermission,
		getPermissions,
		isSuperAdmin,
	}
})
